import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirmedMutation } from '../lib/confirm';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bell, CheckCircle2 } from 'lucide-react';
import { request } from '../api/client';
import { Dropdown, IconButton, CountBadge, Tooltip, cn } from '../ui';
import { resolveNotifyLink } from '../lib/notifyLinks';
import { usePermissionChecker } from '../store/auth';

interface Notif {
  id: string;
  title: string;
  body: string;
  channel: string;
  readAt: string | null;
  /**
   * When the work this message was about finished — set by the server from the RECORD's state, never
   * from anything done in this panel. `readAt` means you looked; `doneAt` means it is dealt with, and
   * only `doneAt` takes a message out of the live list.
   */
  doneAt?: string | null;
  /** Why it is done, in a reader's words ("Complaint closed"). */
  doneReason?: string | null;
  createdAt: string;
  data?: any;
  /** Logical route the mobile clients resolve; the web reads it as a fallback link. */
  deeplink?: string | null;
}

/**
 * Where a notification points, as a web route. Null when it is an announcement with no subject.
 *
 * The `type` + id fallback matters more than it looks: every complaint notification ever sent
 * carries `{ type: 'COMPLAINT', complaintId }` and no link, so tapping one did nothing at all —
 * the message told you a complaint needed you and then refused to open it.
 */
const TYPE_ROUTE: Record<string, { path: string; idKey: string }> = {
  COMPLAINT: { path: '/complaints', idKey: 'complaintId' },
  ORDER: { path: '/orders', idKey: 'orderId' },
  INVOICE: { path: '/billing/invoices', idKey: 'invoiceId' },
};

function notifTarget(n: Notif, can?: (permission: string) => boolean): string | null {
  const raw = n.data?.link ?? n.data?.url ?? n.data?.deeplink ?? n.deeplink;
  // Through the shared resolver: a domain trigger stores a web path, while the broadcast composer
  // stores a LOGICAL code (`invoices/77`) that only the resolver knows is `/billing/invoices/77`.
  // `can` lets it swap a link the reader may not open for the screen they can (DEF-071).
  const resolved = typeof raw === 'string' ? resolveNotifyLink(raw, n.data, can) : null;
  if (resolved) return resolved;
  const route = TYPE_ROUTE[String(n.data?.type ?? '')];
  const id = route ? n.data?.[route.idKey] : null;
  return route && id ? `${route.path}/${id}` : null;
}

/**
 * Is this notification ABOUT the record now on screen?
 *
 * Two ways, because notifications are written by many callers and carry their subject in more than
 * one shape. The link is the normal one. The second covers the older shape that stores an explicit
 * id (`{ type: 'COMPLAINT', complaintId: '123' }`) — matching it here means the several hundred of
 * those already in the table clear as they are read, rather than staying unread forever because
 * they predate the convention.
 */
function isAbout(n: Notif, path: string, can?: (permission: string) => boolean): boolean {
  const target = notifTarget(n, can);
  if (target && (target === path || path.startsWith(`${target}/`))) return true;
  const m = path.match(/^\/([a-z][a-z0-9-]*)\/(\d+)(?:\/|$)/i);
  if (!m || !n.data) return false;
  const camel = m[1].replace(/-([a-z])/g, (_x, c: string) => c.toUpperCase());
  const singular = camel.endsWith('ies') ? `${camel.slice(0, -3)}y` : camel.endsWith('s') ? camel.slice(0, -1) : camel;
  return String(n.data[`${singular}Id`] ?? '') === m[2];
}

// Short relative time ("3m", "2h", "5d").
function relTime(iso: string): string {
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${Math.max(s, 1)}s`;
  const m = Math.round(s / 60); if (m < 60) return `${m}m`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h`;
  const d = Math.round(h / 24); if (d < 30) return `${d}d`;
  return `${Math.round(d / 30)}mo`;
}

const headerIconBtn =
  'border border-line text-content/70 hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700 dark:hover:bg-brand-900/20';

/**
 * The header bell: opens a dropdown of the user's in-app notifications (GET /notifications),
 * with an unread count badge, click-to-mark-read, mark-all-read, and a shortcut to the
 * notification *settings* page (which also lives in the user menu).
 *
 * WHAT THE LIST HOLDS, AND WHAT IT DOES NOT. Only work that is still outstanding. Reading a message
 * marks it read and it STAYS — looking at something is not doing it. It leaves only when the record
 * it is about has stopped waiting on anybody (the complaint is closed, the decision is taken), which
 * the server decides from the record itself, not from anything pressed in here. Nothing is deleted:
 * "Completed" at the foot shows exactly the messages the live list is leaving out, each with the
 * reason it finished.
 */
export function NotificationBell() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const qc = useQueryClient();
  const can = usePermissionChecker();
  const [showDone, setShowDone] = useState(false);

  const { data } = useQuery({
    queryKey: ['notifications-feed'],
    queryFn: () => request<Notif[]>({ url: '/notifications' }),
    refetchInterval: 45_000,
    refetchOnWindowFocus: true,
  });
  /*
   * The completed list is a SECOND query rather than a filter over the first, because the live feed
   * no longer contains those rows at all — and it is only fetched when somebody asks to see them, so
   * the panel costs exactly what it did before for the people who never open it.
   */
  const doneQ = useQuery({
    queryKey: ['notifications-feed', 'done'],
    queryFn: () => request<Notif[]>({ url: '/notifications', params: { view: 'done' } }),
    enabled: showDone,
  });
  /*
   * The unread count comes from the SERVER, counted over every outstanding row — not from the page of
   * rows above. The feed is capped at the newest 100, so counting inside it meant that for anyone with
   * a longer list a new message pushed an older unread one off the end and the pill did not move
   * (certification DEF-028: a Super Admin at 95 received a new order to approve and still read 95).
   * Keyed under 'notifications-feed' so every existing invalidation refreshes it too.
   */
  const unreadQ = useQuery({
    queryKey: ['notifications-feed', 'unread-count'],
    queryFn: () => request<{ unread: number }>({ url: '/notifications/unread-count' }),
    refetchInterval: 45_000,
    refetchOnWindowFocus: true,
  });
  // Memoised: the read-on-arrival effect below depends on it, and `data ?? []` would hand it a
  // fresh array on every render.
  const items = useMemo(() => data ?? [], [data]);
  const pageUnread = useMemo(() => items.filter((n) => !n.readAt).length, [items]);
  // Until the count has loaded, the page's own count is the best available lower bound.
  const unread = unreadQ.data?.unread ?? pageUnread;
  /*
   * The badge and the read-on-arrival effect always run off the LIVE feed, whichever list is on
   * screen: a count that changed because somebody was browsing the completed pile would be lying.
   */
  const shown = showDone ? (doneQ.data ?? []) : items;

  const markRead = useMutation({
    mutationFn: (id: string) => request({ url: `/notifications/${id}/read`, method: 'PATCH' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications-feed'] }),
  });
  const markAllRead = useConfirmedMutation({ title: "Mark every notification as read?", content: "The unread badge clears. The notifications themselves stay in the list.", okText: "Yes, mark all read" }, {
    // One server call over every outstanding row. Marking only the rows on this page left anyone with
    // more than a page of unread pressing "Mark all read" and watching the pill stay up.
    mutationFn: () => request({ url: '/notifications/read-all', method: 'PATCH' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications-feed'] }),
  });

  const onItemClick = (n: Notif) => {
    if (!n.readAt) markRead.mutate(n.id);
    // Through the shared resolver, so a notification that carries only a `deeplink` (the shape the
    // mobile clients are sent) opens here too instead of being a dead click.
    const link = notifTarget(n, can);
    if (link) navigate(link);
  };

  /*
   * READ-ON-ARRIVAL — you looked at the thing, so the notification about it stops being unread.
   *
   * The other half of the same idea as the server's read receipts: acting on a record clears its
   * notification (a global interceptor does that for every domain), and so does simply OPENING the
   * record — however you got there. Reaching an order from a search, a link in a chat or the
   * approvals hub all count as having seen the message about it; only the bell used to.
   *
   * Runs off the feed already in hand, so a page with nothing waiting on it costs no request at
   * all. It marks, at most, the handful of notifications about this one record.
   */
  useEffect(() => {
    const stale = items.filter((n) => !n.readAt && isAbout(n, pathname, can));
    if (!stale.length) return;
    void Promise.all(stale.map((n) => request({ url: `/notifications/${n.id}/read`, method: 'PATCH' })))
      .then(() => qc.invalidateQueries({ queryKey: ['notifications-feed'] }))
      .catch(() => { /* a read receipt is not worth an error message */ });
  }, [pathname, items, qc]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Dropdown
      trigger="click"
      align="end"
      panelClassName="w-[360px] max-w-[92vw] p-0 overflow-hidden"
      panel={
        <div className="flex max-h-[70vh] flex-col">
          <div className="flex items-center justify-between border-b border-line px-3.5 py-2.5">
            <span className="type-body-strong text-content">
              {showDone ? 'Completed' : 'Notifications'}
              {!showDone && unread ? <span className="ml-1 text-brand-600">· {unread} new</span> : null}
            </span>
            <div className="flex items-center gap-1">
              {!showDone && unread > 0 && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); markAllRead.mutate(); }}
                  className="rounded px-1.5 py-0.5 type-caption text-brand-600 hover:bg-surface-2"
                >
                  Mark all read
                </button>
              )}
              {/* No settings cog: there is no notification settings page any more — which channel an
                  event goes out on is Green Bay's decision, not the reader's. */}
            </div>
          </div>

          <div className="overflow-y-auto">
            {shown.length === 0 ? (
              <div className="px-4 py-12 text-center text-sm text-subtle">
                {showDone
                  ? (doneQ.isLoading ? 'Loading…' : 'Nothing has been finished off yet.')
                  : "You're all caught up."}
              </div>
            ) : (
              shown.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  /* Which message this row IS. Two notifications can share a title and even most of a
                     body ("Transfer discrepancy" and "Transfer discrepancy blocking …" differ only in
                     their tail), so a test that finds a row by its text can click the wrong one and
                     then report the wrong link as broken. */
                  data-notif-id={n.id}
                  onClick={() => onItemClick(n)}
                  className={cn(
                    'flex w-full gap-2.5 border-b border-line/60 px-3.5 py-2.5 text-left transition-colors hover:bg-surface-2',
                    !n.readAt && !n.doneAt && 'bg-brand-50/40 dark:bg-brand-900/10',
                  )}
                >
                  <span className={cn('mt-1.5 size-2 shrink-0 rounded-full', n.readAt ? 'bg-transparent' : 'bg-brand-500')} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline justify-between gap-2">
                      <span className={cn('truncate text-sm text-content', n.readAt ? 'font-medium' : 'font-semibold')}>{n.title}</span>
                      <span className="shrink-0 text-2xs text-subtle">{relTime(n.createdAt)}</span>
                    </span>
                    <span className="mt-0.5 block whitespace-pre-line break-words text-xs leading-snug text-subtle line-clamp-3">{n.body}</span>
                    {/* WHAT finished it, not just that something did — "Complaint closed" is the whole
                        reason this row is no longer in the live list, so it is the line to print. */}
                    {n.doneAt && (
                      <span className="mt-1 flex items-center gap-1 text-2xs font-medium text-success-700 dark:text-success-400">
                        <CheckCircle2 className="size-3 shrink-0" />
                        {n.doneReason ?? 'Finished'}
                      </span>
                    )}
                  </span>
                </button>
              ))
            )}
          </div>

          {/*
            A message that leaves the live list has to be findable, or the bell has silently lost
            something the reader remembers being told. This is where it went.
          */}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowDone((v) => !v); }}
            className="flex items-center justify-center gap-1.5 border-t border-line px-3.5 py-2 type-caption text-subtle transition-colors hover:bg-surface-2 hover:text-content"
          >
            {showDone
              ? <><ArrowLeft className="size-3.5" /> Back to outstanding</>
              : <><CheckCircle2 className="size-3.5" /> Completed</>}
          </button>
        </div>
      }
    >
      <span className="relative inline-flex">
        <Tooltip label="Notifications">
          <IconButton aria-label="Notifications" className={headerIconBtn}>
            <Bell />
          </IconButton>
        </Tooltip>
        {unread > 0 && <CountBadge count={unread} className="pointer-events-none absolute -right-1 -top-1" />}
      </span>
    </Dropdown>
  );
}
